import { useEffect, useRef } from "react";
import * as THREE from "three";
import gsap from "gsap";

const COUNT = 8000;

function fill(gen) {
  const pts = new Float32Array(COUNT * 3);
  for (let i = 0; i < COUNT; i++) {
    const [x, y] = gen();
    pts[i * 3] = x;
    pts[i * 3 + 1] = y;
    pts[i * 3 + 2] = (Math.random() - 0.5) * 0.45;
  }
  return pts;
}

const seg = (x1, y1, x2, y2, thick) => {
  const t = Math.random();
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  const px = -dy / len, py = dx / len;
  const o = (Math.random() - 0.5) * thick;
  return [x1 + dx * t + px * o, y1 + dy * t + py * o];
};

function keyPoints() {
  return fill(() => {
    const r = Math.random();
    let x, y;
    if (r < 0.42) {
      const a = Math.random() * Math.PI * 2;
      const rad = 0.52 + Math.random() * 0.33;
      x = Math.cos(a) * rad; y = 1.15 + Math.sin(a) * rad;
    } else if (r < 0.75) {
      x = (Math.random() - 0.5) * 0.32; y = -1.55 + Math.random() * 2.05;
    } else if (r < 0.9) {
      x = 0.16 + Math.random() * 0.42; y = -1.55 + Math.random() * 0.16;
    } else {
      x = 0.16 + Math.random() * 0.3; y = -1.18 + Math.random() * 0.16;
    }
    return [x, y - 0.2];
  });
}

const STAR_POLY = (outer, inner) => {
  const v = [];
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const r = i % 2 === 0 ? outer : inner;
    v.push([Math.cos(a) * r, -Math.sin(a) * r]);
  }
  return v;
};

function inPoly(x, y, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

const starSample = (outer, inner, ox = 0, oy = 0) => {
  const poly = STAR_POLY(outer, inner);
  for (;;) {
    const x = (Math.random() - 0.5) * outer * 2.1;
    const y = (Math.random() - 0.5) * outer * 2.1;
    if (inPoly(x, -y, poly)) return [x + ox, y + oy];
  }
};

const starPoints = () => fill(() => starSample(1.7, 0.68, 0, 0.15));

function housePoints() {
  return fill(() => {
    const r = Math.random();
    if (r < 0.18) return seg(-1.25, 0.35, 0, 1.5, 0.2);
    if (r < 0.36) return seg(0, 1.5, 1.25, 0.35, 0.2);
    if (r < 0.52) return seg(-1.05, 0.35, -1.05, -1.35, 0.2);
    if (r < 0.68) return seg(1.05, 0.35, 1.05, -1.35, 0.2);
    if (r < 0.84) return seg(-1.05, -1.35, 1.05, -1.35, 0.2);
    if (r < 0.89) return seg(-0.25, -1.35, -0.25, -0.5, 0.14);
    if (r < 0.94) return seg(-0.25, -0.5, 0.25, -0.5, 0.14);
    return seg(0.25, -1.35, 0.25, -0.5, 0.14);
  });
}

function sealPoints() {
  return fill(() => {
    if (Math.random() < 0.5) {
      const a = Math.random() * Math.PI * 2;
      const rad = 1.38 + Math.random() * 0.34;
      return [Math.cos(a) * rad, Math.sin(a) * rad];
    }
    return starSample(0.95, 0.4, 0, 0);
  });
}

function questionPoints() {
  return fill(() => {
    const r = Math.random();
    if (r < 0.62) {
      const a = Math.PI * 1.05 - Math.random() * Math.PI * 1.42;
      const rad = 0.82 + (Math.random() - 0.5) * 0.24;
      return [Math.cos(a) * rad, 0.72 + Math.sin(a) * rad];
    }
    if (r < 0.85) return seg(0.03, 0.15, 0.03, -0.55, 0.24);
    const a = Math.random() * Math.PI * 2;
    const rad = Math.sqrt(Math.random()) * 0.2;
    return [0.03 + Math.cos(a) * rad, -1.15 + Math.sin(a) * rad];
  });
}

function checkPoints() {
  return fill(() => {
    if (Math.random() < 0.38) return seg(-1.45, 0.05, -0.4, -1.0, 0.26);
    return seg(-0.4, -1.0, 1.55, 1.05, 0.26);
  });
}

const scatterPoints = () => {
  const pts = new Float32Array(COUNT * 3);
  for (let i = 0; i < COUNT; i++) {
    const a = Math.random() * Math.PI * 2;
    const b = Math.acos(2 * Math.random() - 1);
    const rad = 4 + Math.random() * 5;
    pts[i * 3] = rad * Math.sin(b) * Math.cos(a);
    pts[i * 3 + 1] = rad * Math.sin(b) * Math.sin(a);
    pts[i * 3 + 2] = rad * Math.cos(b) - 2;
  }
  return pts;
};

const VERT = `
attribute vec3 aFrom; attribute vec3 aTo; attribute vec3 aScatter; attribute vec3 aStart;
attribute float aSize; attribute float aSeed; attribute float aBright;
uniform float uAssemble; uniform float uMix; uniform float uTime; uniform float uPixelRatio;
uniform vec2 uMouse; uniform float uRepel;
varying float vBright;
void main() {
  float e = smoothstep(0.0, 1.0, uMix);
  vec3 shapePos = mix(aFrom, aTo, e);
  float burst = sin(uMix * 3.14159) * (0.3 + fract(aSeed * 5.31) * 0.5);
  shapePos = mix(shapePos, aScatter, burst);
  float ae = 1.0 - pow(1.0 - uAssemble, 3.0);
  vec3 pos = mix(aStart, shapePos, ae);
  float speed = 0.6 + fract(aSeed * 7.13) * 0.9;
  pos.x += sin(uTime * speed + aSeed * 43.7) * 0.035;
  pos.y += cos(uTime * (speed * 0.8) + aSeed * 91.3) * 0.035;
  pos.z += sin(uTime * (speed * 0.5) + aSeed * 17.9) * 0.03;
  vec2 diff = pos.xy - uMouse;
  float dist = length(diff);
  float force = smoothstep(1.15, 0.0, dist) * uRepel * (0.55 + fract(aSeed * 3.37) * 0.45);
  pos.xy += (diff / max(dist, 0.001)) * force;
  vec4 mv = modelViewMatrix * vec4(pos, 1.0);
  gl_Position = projectionMatrix * mv;
  gl_PointSize = aSize * uPixelRatio * (5.2 / -mv.z);
  vBright = aBright;
}`;

const FRAG = `
varying float vBright;
void main() {
  float d = length(gl_PointCoord - 0.5);
  float alpha = smoothstep(0.5, 0.08, d);
  float core = smoothstep(0.22, 0.0, d);
  vec3 red = vec3(1.0, 0.05, 0.05) * vBright;
  vec3 color = mix(red, vec3(1.0, 0.75, 0.7), core * 0.55);
  gl_FragColor = vec4(color, alpha * 0.92);
}`;

const GENERATORS = { key: keyPoints, star: starPoints, house: housePoints, seal: sealPoints, question: questionPoints, check: checkPoints };
const DEFAULT_SHAPES = ["key", "star", "house", "seal", "question", "check"];
const DEFAULT_ANCHORS = [
  null,
  '[data-testid="manifesto-section"]',
  "#learn",
  "#host",
  "#faq",
  "#register",
];

export const ParticleField = ({ shapeNames = DEFAULT_SHAPES, anchorSelectors = DEFAULT_ANCHORS, xOffset = 1.45 }) => {
  const mountRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const mount = mountRef.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, 1, 0.1, 100);
    camera.position.z = 5.2;
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);

    const shapes = shapeNames.map((n) => GENERATORS[n]());

    const geo = new THREE.BufferGeometry();
    const sizes = new Float32Array(COUNT);
    const seeds = new Float32Array(COUNT);
    const brights = new Float32Array(COUNT);
    for (let i = 0; i < COUNT; i++) {
      sizes[i] = 1.4 + Math.random() * 1.8;
      seeds[i] = Math.random();
      brights[i] = 0.9 + Math.random() * 0.5;
    }
    geo.setAttribute("position", new THREE.BufferAttribute(shapes[0].slice(), 3));
    const fromAttr = new THREE.BufferAttribute(shapes[0].slice(), 3);
    const toAttr = new THREE.BufferAttribute(shapes[1].slice(), 3);
    geo.setAttribute("aFrom", fromAttr);
    geo.setAttribute("aTo", toAttr);
    geo.setAttribute("aScatter", new THREE.BufferAttribute(scatterPoints(), 3));
    geo.setAttribute("aStart", new THREE.BufferAttribute(scatterPoints(), 3));
    geo.setAttribute("aSize", new THREE.BufferAttribute(sizes, 1));
    geo.setAttribute("aSeed", new THREE.BufferAttribute(seeds, 1));
    geo.setAttribute("aBright", new THREE.BufferAttribute(brights, 1));

    const uniforms = {
      uAssemble: { value: 0 },
      uMix: { value: 0 },
      uTime: { value: 0 },
      uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
      uMouse: { value: new THREE.Vector2(999, 999) },
      uRepel: { value: 0 },
    };
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const points = new THREE.Points(geo, mat);
    scene.add(points);

    gsap.to(uniforms.uAssemble, { value: 1, duration: 1.4, ease: "power2.out", delay: 0.3 });

    const anchors = new Array(anchorSelectors.length).fill(0);
    const measure = () => {
      const vh = window.innerHeight;
      anchors[0] = 0;
      for (let i = 1; i < anchorSelectors.length; i++) {
        const el = document.querySelector(anchorSelectors[i]);
        anchors[i] = el
          ? Math.max(anchors[i - 1] + 10, el.getBoundingClientRect().top + window.scrollY - vh * 0.35)
          : anchors[i - 1] + vh * 2;
      }
    };

    let halfH = Math.tan((camera.fov * Math.PI) / 360) * camera.position.z;
    let halfW = halfH;
    const resize = () => {
      const w = mount.clientWidth, h = mount.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      points.position.x = w >= 1024 ? xOffset : 0;
      halfW = halfH * camera.aspect;
      measure();
    };
    resize();
    window.addEventListener("resize", resize);
    const settle = setTimeout(measure, 900);
    const remeasure = setInterval(measure, 2500);

    let targetMx = 0, targetMy = 0, mx = 0, my = 0, targetStr = 0, str = 0;
    const onMouseMove = (e) => {
      targetMx = (e.clientX / window.innerWidth) * 2 - 1;
      targetMy = -((e.clientY / window.innerHeight) * 2 - 1);
      targetStr = 1;
    };
    const onMouseLeave = () => { targetStr = 0; };
    window.addEventListener("mousemove", onMouseMove);
    document.documentElement.addEventListener("mouseleave", onMouseLeave);

    const clock = new THREE.Clock();
    let raf, smoothY = window.scrollY, currentSeg = 0;
    const loop = () => {
      smoothY += (window.scrollY - smoothY) * 0.08;
      let i = 0;
      while (i < anchors.length - 2 && smoothY >= anchors[i + 1]) i++;
      const a0 = anchors[i], a1 = anchors[i + 1];
      let t = a1 > a0 ? (smoothY - a0) / (a1 - a0) : 1;
      t = Math.min(Math.max(t, 0), 1);
      if (i !== currentSeg) {
        fromAttr.array.set(shapes[i]);
        toAttr.array.set(shapes[i + 1]);
        fromAttr.needsUpdate = true;
        toAttr.needsUpdate = true;
        currentSeg = i;
      }
      uniforms.uMix.value = t < 0.28 ? 0 : t > 0.86 ? 1 : (t - 0.28) / 0.58;
      uniforms.uTime.value = clock.getElapsedTime();
      mx += (targetMx - mx) * 0.1;
      my += (targetMy - my) * 0.1;
      str += (targetStr - str) * 0.06;
      uniforms.uMouse.value.set(mx * halfW - points.position.x, my * halfH);
      uniforms.uRepel.value = 0.55 * str;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      clearInterval(remeasure);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouseMove);
      document.documentElement.removeEventListener("mouseleave", onMouseLeave);
      geo.dispose();
      mat.dispose();
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="absolute inset-0" data-testid="particle-field" />;
};
